import React, { useState, useEffect } from 'react';
import { Calculator, AlertTriangle, CheckCircle, Search } from 'lucide-react';
import { getAllBailSchedule, CATEGORY_NAMES, getCategoryName } from '../../../services/bailScheduleService';

const BailEstimator = ({ language = 'en' }) => {
  const [chargeType, setChargeType] = useState('');
  const [bailAmount, setBailAmount] = useState('');
  const [paymentPlan, setPaymentPlan] = useState('full');
  const [results, setResults] = useState(null);

  // New state for database integration
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [bailScheduleData, setBailScheduleData] = useState([]);
  const [filteredCharges, setFilteredCharges] = useState([]);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load bail schedule data on component mount
  useEffect(() => {
    loadBailSchedule();
  }, []);

  // Filter charges based on search and category
  useEffect(() => {
    filterCharges();
  }, [searchTerm, selectedCategory, bailScheduleData]);

  const loadBailSchedule = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getAllBailSchedule();
      if (response?.success) {
        setBailScheduleData(response?.data || []);
        setFilteredCharges(response?.data || []);
      } else {
        setError(response?.error || 'Failed to load bail schedule');
      }
    } catch (err) {
      setError('Failed to load bail schedule data');
      console.error('Error loading bail schedule:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterCharges = () => {
    let filtered = [...bailScheduleData];

    // Filter by category
    if (selectedCategory) {
      filtered = filtered?.filter(charge => charge?.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm?.toLowerCase();
      filtered = filtered?.filter(charge => 
        charge?.offense_description?.toLowerCase()?.includes(searchLower) ||
        charge?.code_section?.toLowerCase()?.includes(searchLower)
      );
    }

    setFilteredCharges(filtered);
  };

  const handleChargeSelect = (charge) => {
    setSelectedCharge(charge);
    setBailAmount(charge?.bail_amount?.toString());
    setChargeType('database');
  };

  const calculateEstimate = () => {
    let estimatedBail = selectedCharge?.bail_amount || parseFloat(bailAmount) || 0;
    
    if (estimatedBail === 0) {
      setResults(null);
      return;
    }

    const bondFee = estimatedBail * 0.10; // 10% California rate
    const collateralRequired = estimatedBail > 25000;
    
    let paymentOptions = {
      full: {
        downPayment: bondFee,
        monthlyPayment: 0,
        totalCost: bondFee
      },
      plan3: {
        downPayment: bondFee * 0.20,
        monthlyPayment: (bondFee * 0.80) / 3,
        totalCost: bondFee
      },
      plan6: {
        downPayment: bondFee * 0.15,
        monthlyPayment: (bondFee * 0.85) / 6,
        totalCost: bondFee
      },
      plan12: {
        downPayment: bondFee * 0.10,
        monthlyPayment: (bondFee * 0.90) / 12,
        totalCost: bondFee
      }
    };

    setResults({
      bailAmount: estimatedBail,
      bondFee,
      collateralRequired,
      paymentOptions,
      releaseTime: estimatedBail < 10000 ? '2-3 hours' : '3-4 hours',
      chargeInfo: selectedCharge
    });
  };

  const text = {
    en: {
      title: 'Bail Amount Estimator',
      subtitle: 'Get an instant estimate of bail costs and payment options',
      chargeLabel: 'Type of Charge',
      customLabel: 'Enter Bail Amount',
      paymentLabel: 'Preferred Payment Option',
      calculate: 'Calculate Estimate',
      results: 'Your Estimate',
      bailAmount: 'Estimated Bail Amount',
      bondFee: 'Our Fee (10%)',
      collateral: 'Collateral Required',
      releaseTime: 'Estimated Release Time',
      downPayment: 'Down Payment',
      monthlyPayment: 'Monthly Payment',
      totalCost: 'Total Cost',
      disclaimer: 'Estimates are based on typical bail schedules. Actual amounts may vary.',
      callNow: 'Call for Exact Quote',
      searchCharges: 'Search criminal charges...',
      filterCategory: 'Filter by category',
      allCategories: 'All Categories',
      codeSection: 'Code Section',
      selectedCharge: 'Selected Charge',
      noResults: 'No charges found matching your search',
      paymentOptions: {
        full: 'Full Payment',
        plan3: '3-Month Plan',
        plan6: '6-Month Plan', 
        plan12: '12-Month Plan'
      }
    },
    es: {
      title: 'Calculadora de Monto de Fianza',
      subtitle: 'Obtenga una estimación instantánea de costos de fianza y opciones de pago',
      chargeLabel: 'Tipo de Cargo',
      customLabel: 'Ingrese Monto de Fianza',
      paymentLabel: 'Opción de Pago Preferida',
      calculate: 'Calcular Estimación',
      results: 'Su Estimación',
      bailAmount: 'Monto de Fianza Estimado',
      bondFee: 'Nuestro Honorario (10%)',
      collateral: 'Garantía Requerida',
      releaseTime: 'Tiempo Estimado de Liberación',
      downPayment: 'Pago Inicial',
      monthlyPayment: 'Pago Mensual',
      totalCost: 'Costo Total',
      disclaimer: 'Las estimaciones se basan en horarios de fianza típicos. Los montos reales pueden variar.',
      callNow: 'Llamar para Cotización Exacta',
      searchCharges: 'Buscar cargos criminales...',
      filterCategory: 'Filtrar por categoría',
      allCategories: 'Todas las Categorías',
      codeSection: 'Sección del Código',
      selectedCharge: 'Cargo Seleccionado',
      noResults: 'No se encontraron cargos que coincidan con su búsqueda',
      paymentOptions: {
        full: 'Pago Completo',
        plan3: 'Plan de 3 Meses',
        plan6: 'Plan de 6 Meses',
        plan12: 'Plan de 12 Meses'
      }
    }
  };

  const currentText = text?.[language] || text?.en;

  return (
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center mb-12">
        <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-6 flex items-center justify-center">
          <Calculator className="w-10 h-10 mr-4 text-blue-600" aria-hidden="true" />
          {currentText?.title}
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
          {currentText?.subtitle}
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" aria-hidden="true" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Calculator Form */}
          <div>
            <div className="space-y-6">
              {/* Search Bar */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {currentText?.searchCharges}
                </label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0"
                    placeholder={language === 'es' ? 'Ej: DUI, Asalto, Robo...' : 'E.g., DUI, Assault, Burglary...'}
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {currentText?.filterCategory}
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e?.target?.value)}
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-0"
                >
                  <option value="">{currentText?.allCategories}</option>
                  {Object.entries(CATEGORY_NAMES)?.map(([key, name]) => (
                    <option key={key} value={key}>{name}</option>
                  ))}
                </select>
              </div>

              {/* Charges List */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {currentText?.chargeLabel}
                </label>
                <div className="max-h-80 overflow-y-auto border-2 border-gray-300 rounded-xl">
                  {isLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      {language === 'es' ? 'Cargando...' : 'Loading...'}
                    </div>
                  ) : filteredCharges?.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {filteredCharges?.map((charge) => (
                        <button
                          key={charge?.id}
                          onClick={() => handleChargeSelect(charge)}
                          className={`w-full text-left p-4 hover:bg-blue-50 transition-colors ${
                            selectedCharge?.id === charge?.id ? 'bg-blue-100 border-l-4 border-blue-600' : ''
                          }`}
                        >
                          <div className="font-semibold text-gray-900 mb-1">
                            {charge?.offense_description}
                          </div>
                          <div className="text-sm text-gray-600 mb-1">
                            {currentText?.codeSection}: {charge?.code_section}
                          </div>
                          <div className="text-sm font-bold text-blue-600">
                            ${charge?.bail_amount?.toLocaleString()}
                          </div>
                          {charge?.notes && (
                            <div className="text-xs text-gray-500 mt-1 italic">
                              {charge?.notes}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      {currentText?.noResults}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Charge Info */}
              {selectedCharge && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="font-bold text-blue-900 mb-2">{currentText?.selectedCharge}</h4>
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold">{selectedCharge?.offense_description}</p>
                    <p className="text-blue-600">{selectedCharge?.code_section}</p>
                    <p className="text-xs mt-1">{getCategoryName(selectedCharge?.category)}</p>
                  </div>
                </div>
              )}

              {/* Payment Plan Selection */}
              <div>
                <label className="block text-sm font-bold text-gray-900 mb-3">
                  {currentText?.paymentLabel}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(currentText?.paymentOptions)?.map(([key, label]) => (
                    <label key={key} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name="paymentPlan"
                        value={key}
                        checked={paymentPlan === key}
                        onChange={(e) => setPaymentPlan(e?.target?.value)}
                        className="sr-only"
                      />
                      <div className={`flex-1 p-3 border-2 rounded-xl text-center font-semibold transition-all ${
                        paymentPlan === key 
                          ? 'border-blue-500 bg-blue-50 text-blue-700' :'border-gray-300 hover:border-blue-300'
                      }`}>
                        {label}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateEstimate}
                disabled={!selectedCharge && !bailAmount}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
              >
                <Calculator className="w-6 h-6 mr-3" aria-hidden="true" />
                {currentText?.calculate}
              </button>
            </div>
          </div>

          {/* Results Display */}
          <div>
            {results ? (
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mr-3" aria-hidden="true" />
                  {currentText?.results}
                </h3>

                <div className="space-y-6">
                  {/* Charge Information */}
                  {results?.chargeInfo && (
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-sm text-gray-600 mb-1 font-semibold">
                        {results?.chargeInfo?.offense_description}
                      </div>
                      <div className="text-xs text-gray-500">
                        {results?.chargeInfo?.code_section} - {getCategoryName(results?.chargeInfo?.category)}
                      </div>
                    </div>
                  )}

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-sm text-gray-600 mb-1">{currentText?.bailAmount}</div>
                      <div className="text-2xl font-bold text-gray-900">
                        ${results?.bailAmount?.toLocaleString()}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-blue-200">
                      <div className="text-sm text-gray-600 mb-1">{currentText?.bondFee}</div>
                      <div className="text-2xl font-bold text-blue-600">
                        ${results?.bondFee?.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Selected Payment Plan Details */}
                  <div className="bg-white rounded-xl p-6 border-2 border-blue-300">
                    <h4 className="font-bold text-gray-900 mb-4">
                      {currentText?.paymentOptions?.[paymentPlan]}
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">{currentText?.downPayment}:</span>
                        <div className="font-bold text-green-600 text-lg">
                          ${results?.paymentOptions?.[paymentPlan]?.downPayment?.toFixed(0)}
                        </div>
                      </div>
                      {results?.paymentOptions?.[paymentPlan]?.monthlyPayment > 0 && (
                        <div>
                          <span className="text-gray-600">{currentText?.monthlyPayment}:</span>
                          <div className="font-bold text-blue-600 text-lg">
                            ${results?.paymentOptions?.[paymentPlan]?.monthlyPayment?.toFixed(0)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between py-2 border-b border-blue-200">
                      <span className="text-gray-600">{currentText?.collateral}:</span>
                      <span className={results?.collateralRequired ? 'text-amber-600 font-semibold' : 'text-green-600 font-semibold'}>
                        {results?.collateralRequired ? (language === 'es' ? 'Sí' : 'Yes') : (language === 'es' ? 'No' : 'No')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-blue-200">
                      <span className="text-gray-600">{currentText?.releaseTime}:</span>
                      <span className="text-blue-600 font-semibold">{results?.releaseTime}</span>
                    </div>
                  </div>

                  {/* Call to Action */}
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                    <div className="text-sm text-red-600 mb-2">{currentText?.disclaimer}</div>
                    <a 
                      href="tel:+19162563468"
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold text-sm transition-colors inline-flex items-center"
                    >
                      {currentText?.callNow}
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-2xl p-8 border-2 border-dashed border-gray-300 text-center">
                <Calculator className="w-16 h-16 text-gray-400 mx-auto mb-4" aria-hidden="true" />
                <h3 className="text-xl font-bold text-gray-500 mb-2">
                  {language === 'es' ? 'Seleccione un cargo para calcular' : 'Select a charge to calculate'}
                </h3>
                <p className="text-gray-400">
                  {language === 'es' ?'Busque y seleccione un cargo criminal para ver su estimación de fianza' :'Search and select a criminal charge to see your bail estimate'
                  }
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Important Notice */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mr-3 mt-1" aria-hidden="true" />
            <div>
              <h4 className="font-bold text-amber-800 mb-2">
                {language === 'es' ? 'Aviso Importante' : 'Important Notice'}
              </h4>
              <p className="text-amber-700 text-sm leading-relaxed">
                {language === 'es' ?'Esta calculadora proporciona estimaciones basadas en el Calendario de Fianzas del Condado de Sacramento 2025. Los montos reales de fianza pueden variar según las circunstancias específicas del caso, el historial criminal y la discreción del juez. Para obtener información exacta sobre fianza, llámenos las 24 horas al (916) 256-3468.' :'This calculator provides estimates based on the Sacramento County Bail Schedule 2025. Actual bail amounts may vary based on specific case circumstances, criminal history, and judge discretion. For exact bail information, call us 24/7 at (916) 256-3468.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BailEstimator;